/* import __COLOCATED_TEMPLATE__ from './shopify.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import { restartableTask } from 'ember-concurrency-decorators';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { isValidEmail } from 'embercom/lib/email';
import { isValidOrderName } from 'embercom/lib/inbox/shopify/order';
import { taskFor } from 'ember-concurrency-ts';
import type Session from 'embercom/services/session';
import { inject as service } from '@ember/service';
import { type TaskGenerator } from 'ember-concurrency';
import type User from 'embercom/objects/inbox/user';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';
import type IntlService from 'embercom/services/intl';
import type Conversation from 'embercom/objects/inbox/conversation';
import type ConversationAttributeDescriptor from 'embercom/objects/inbox/conversation-attribute-descriptor';
import ConversationAttributeSummary from 'embercom/objects/inbox/conversation-attribute-summary';
import type InboxApi from 'embercom/services/inbox-api';
import type InboxState from 'embercom/services/inbox-state';
import { TrackedArray, TrackedSet } from 'tracked-built-ins';

interface Args {
  user?: User;
  conversation: Conversation;
  conversationAttributeDescriptors?: ConversationAttributeDescriptor[];
  isOpen: boolean;
  onOpenSectionChange: () => void;
  onExpandedSectionChange: () => void;
  isExpanded: boolean;
  name: string;
  conversationId: number;
  userId: string;
}

interface Signature {
  Args: Args;
}

interface ShopifyError {
  key: string;
}

export interface ShopifyCustomer {
  id: number | null;
  created_at: string | null;
  email: string | null;
  name: string;
  phone: string | null;
  lifetime_spent: string;
  last_order: string | null;
  currency_symbol: string;
  default_address: ShippingAddress | null;
}

export interface ShopifyLineItem {
  id: number;
  title: string;
  subtitle: string;
  tertiary_text: string;
  tracking_number: string | null;
  tracking_url: string | null;
  sku: string | null;
  productVariant: {
    variant_id: string;
  };
  quantity: number;
  quantity_refundable?: number;
  already_refunded?: number;
  image_src: string | null;
  price: string;
  total_discount?: string;
}

export interface ShippingAddress {
  first_name: string | null;
  last_name: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  zip: string | null;
  phone: string | null;
}

export interface ShopifyOrder {
  app_id?: string;
  id: number | null;
  store: string | null;
  created_at: string | null;
  email: string | null;
  name: string | null;
  fulfillment_status_text: string;
  financial_status_text: string;
  image_url: string;
  subtotal: string;
  shipping: string;
  tags: string;
  tax: string;
  total: string;
  line_items: ShopifyLineItem[];
  order_url: string;
  order_status_url: string | null;
  shipping_address: ShippingAddress | null;
  billing_address: ShippingAddress | null;
  addresses_match: boolean;
  refundable: boolean;
  replaceable?: boolean;
  gift_card_compensatable?: boolean;
  shipping_refunded?: boolean;
}

interface ShopifyCustomerDataResponse {
  customer: ShopifyCustomer;
  customer_orders: ShopifyOrder[];
  all_customer_orders_url: string;
}

interface AccessScopeCheckResponse {
  should_request_more_scopes: boolean;
}

interface ShopifyIntegration {
  id: string;
  shop_id: string;
  intercom_app_id: string;
  shopify_domain: string;
  installation_status: string;
}

export default class Shopify extends Component<Signature> {
  @service declare session: Session;
  @service declare inboxApi: InboxApi;
  @service declare inboxState: InboxState;
  @service declare intl: IntlService;

  @tracked shopifyCustomer?: ShopifyCustomer;
  @tracked shopifyCustomerOrdersUrl?: string;
  @tracked orders = new TrackedArray<ShopifyOrder>([]);

  @tracked shopifyError?: ShopifyError;
  @tracked shouldRequestMoreScopes?: boolean;

  @tracked previousUserId: string | null | undefined;
  @tracked previousConversationId: number | null | undefined;

  @tracked shopifyEmailOrOrderName?: string | null | undefined = this.args.user?.email;
  @tracked shopifyDomain?: string | null | unknown =
    this.args.user?.attributes?.custom_data?.shopify_domain;

  @tracked selectedTab = 'customer';
  @tracked showCreateOrderModal = false;
  @tracked showAllOrders = false;
  @tracked expandCustomer = false;
  @tracked isOrderSearch = false;
  @tracked expandedOrderIds = new TrackedSet<number>();
  @tracked connectedShops: { value: string; text: string; isDisabled?: boolean }[] = [];
  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.fetchShopifyDataTask).perform();
    taskFor(this.checkAccessScope).perform();
  }

  get storeName() {
    return typeof this.shopifyDomain === 'string' ? this.shopifyDomain.split('.')[0] : undefined;
  }

  get hasOrders() {
    return this.orders && this.orders.length > 0;
  }

  get userShopifyDomain() {
    return this.args.user?.attributes?.custom_data?.shopify_domain;
  }

  get conversationShopifyDomainAttribute() {
    return this.args.conversation.attributes.find(
      (attr) => attr.descriptor.name === 'shopify_domain',
    );
  }

  get conversationShopifyDomainValue() {
    if (this.conversationShopifyDomainAttribute) {
      return this.conversationShopifyDomainAttribute.value;
    }

    return null;
  }

  get defaultValue() {
    return this.conversationShopifyDomainValue || this.shopifyDomain || '';
  }

  get emailIsValid() {
    return isValidEmail(this.shopifyEmailOrOrderName);
  }

  get canUseShopifyMultiStore() {
    return this.session.workspace.isFeatureEnabled('shopify-multi-store');
  }

  get dropDownOptions() {
    return [
      {
        value: '',
        text: this.intl.t('inbox.conversation-details-sidebar.shopify.select-shop'),
        isDisabled: true,
      },
      ...this.connectedShops,
    ];
  }

  get isShopSelected() {
    if (this.canUseShopifyMultiStore) {
      return this.shopifyDomain || this.conversationShopifyDomainValue;
    }

    return true;
  }

  get newestOrder(): ShopifyOrder | undefined {
    if (!this.isOrderSearch) {
      return this.orders?.[0];
    }

    let searchTerm = this.shopifyEmailOrOrderName?.trim();
    let formattedTerm = searchTerm?.startsWith('#') ? searchTerm : `#${searchTerm}`;
    return this.orders?.find((order) => order.name === formattedTerm) || this.orders?.[0];
  }

  @restartableTask
  *fetchShopifyDataTask(shop_domain?: string): TaskGenerator<void> {
    this.shopifyCustomer = undefined;
    this.shopifyError = undefined;

    this.shopifyDomain =
      shop_domain && shop_domain !== ''
        ? shop_domain
        : this.conversationShopifyDomainValue || this.userShopifyDomain;

    this.showAllOrders = false;
    this.expandCustomer = false;
    this.expandedOrderIds.clear();

    if (this.canUseShopifyMultiStore) {
      yield taskFor(this.loadShopifyDomains).perform();
    }

    if (
      !isValidEmail(this.shopifyEmailOrOrderName) &&
      !isValidOrderName(this.shopifyEmailOrOrderName)
    ) {
      this.shopifyError = { key: 'invalid_email_or_order_name' };
      return;
    }

    let data = {
      app_id: this.session.workspace.id,
    } as {
      app_id: string;
      user_email?: string | null;
      order_name?: string | null;
      shopify_domain?: string | null | unknown;
    };

    if (this.shopifyDomain) {
      data['shopify_domain'] = this.shopifyDomain;
    }

    if (this.emailIsValid) {
      data['user_email'] = this.shopifyEmailOrOrderName;
      this.isOrderSearch = false;
    } else {
      data['order_name'] = this.shopifyEmailOrOrderName;
      this.isOrderSearch = true;
    }

    try {
      let shopifyResponse = yield ajax({
        url: '/ember/inbox/shopify/shopify_customer_data',
        type: 'GET',
        data,
      }) as ShopifyCustomerDataResponse;

      this.shopifyCustomer = shopifyResponse.customer;
      this.orders = new TrackedArray(shopifyResponse.customer_orders);
      this.shopifyCustomerOrdersUrl = shopifyResponse.all_customer_orders_url;
    } catch (error) {
      captureException(error);
      if (error.jqXHR?.responseJSON?.error) {
        this.shopifyError = error.jqXHR.responseJSON.error;
      } else {
        this.shopifyError = { key: 'unknown' };
      }
    }
  }

  @restartableTask
  *checkAccessScope(): TaskGenerator<void> {
    try {
      let accessScopeResponse = yield ajax({
        url: '/ember/inbox/shopify/access_scope_check',
        type: 'GET',
        data: {
          app_id: this.session.workspace.id,
        },
      }) as AccessScopeCheckResponse;

      this.shouldRequestMoreScopes = accessScopeResponse.should_request_more_scopes;
    } catch (error) {
      captureException(error);
    }
  }

  @restartableTask
  *loadShopifyDomains(): TaskGenerator<void> {
    try {
      let response = yield ajax({
        url: '/ember/shopify/integrations',
        type: 'GET',
        data: {
          app_id: this.session.workspace.id,
        },
      }) as ShopifyIntegration[];

      this.connectedShops = response
        .filter((shop: ShopifyIntegration) => shop.installation_status === 'OK')
        .map((shop: ShopifyIntegration) => ({
          value: shop.shopify_domain,
          text: shop.shopify_domain,
        }));

      // If the workspace has only one shop connected, default it to that shop
      if (this.connectedShops.length === 1 && !this.shopifyDomain) {
        this.shopifyDomain = this.connectedShops[0].value;
      }
    } catch (error) {
      captureException(error);
    }
  }

  @action
  intercomUserChanged(user: User | undefined, conversation: Conversation | undefined) {
    this.shopifyEmailOrOrderName = this.args.user?.email;

    if (this.previousUserId !== user?.id || this.previousConversationId !== conversation?.id) {
      this.previousUserId = user?.id;
      this.previousConversationId = conversation?.id;
      taskFor(this.fetchShopifyDataTask).perform();
    }
  }

  @action
  focusOnEmailSearchInput() {
    let emailOrOrderSearchInput = document.querySelector<HTMLElement>(
      '.js__shopify-email-or-order-search-input',
    );
    emailOrOrderSearchInput && emailOrOrderSearchInput.focus();
  }

  @action
  async shopSelectedChanged(value: string) {
    await this.updateConversationAttribute(value);
    taskFor(this.fetchShopifyDataTask).perform(value);
  }

  // Updates the conversation's shopify_domain attribute
  // when user CDA: 1. is not set or 2. when it does not point to the right shop
  // Option 2 would happen when user places order on 2 differnt stores
  // but we always return the most recent user (which might not have the right shopify domain)
  async updateConversationAttribute(value: string) {
    let descriptor = this.args.conversationAttributeDescriptors?.find(
      (descriptor) => descriptor.name === 'shopify_domain',
    );

    if (descriptor) {
      let attribute = new ConversationAttributeSummary(value, descriptor);

      await this.inboxApi.updateAttribute(this.args.conversation.id, attribute);
    }
  }

  @action
  toggleExpandCustomer() {
    this.expandCustomer = !this.expandCustomer;
  }

  @action
  toggleOrderExpanded(orderId: number) {
    if (this.expandedOrderIds.has(orderId)) {
      this.expandedOrderIds.delete(orderId);
    } else {
      this.expandedOrderIds.add(orderId);
    }
  }

  @action
  setShowAllOrders() {
    this.showAllOrders = true;
  }

  @action
  backButtonClicked() {
    if (this.showAllOrders) {
      this.showAllOrders = false;
    } else {
      this.expandCustomer = false;
    }
    this.expandedOrderIds.clear();
  }

  @action
  updateExpandedOrderShippingAddress(orderId: number, newShippingAddress: ShippingAddress) {
    let index = this.orders.findIndex((o) => o.id !== null && Number(o.id) === Number(orderId));
    if (index !== -1) {
      this.orders[index] = { ...this.orders[index], shipping_address: newShippingAddress };
    }
  }

  @action
  handleRefundSuccess() {
    taskFor(this.fetchShopifyDataTask).perform();
  }

  @action
  handleReplacementSuccess() {
    taskFor(this.fetchShopifyDataTask).perform();
  }

  @action
  handleGiftCardSuccess() {
    taskFor(this.fetchShopifyDataTask).perform();
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationDetailsSidebar::SidebarSections::Shopify': typeof Shopify;
    'inbox2/conversation-details-sidebar/sidebar-sections/shopify': typeof Shopify;
  }
}
